import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { Key, Plus, Copy, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SubNav } from '@/components/ui/sub-nav';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.frolic.so';

interface Token {
  id: string;
  name: string;
  prefix: string;
  createdAt: string;
  value?: string;
}

function TokensPage() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [newToken, setNewToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['tokens'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/v1/auth/tokens`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch tokens');
      return response.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (name: string) => {
      const response = await fetch(`${API_URL}/v1/auth/tokens`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ name }),
      });
      if (!response.ok) throw new Error('Failed to create token');
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
      setNewToken(data.token.value);
      setTokenName('');
      setShowCreate(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (tokenId: string) => {
      const response = await fetch(`${API_URL}/v1/auth/tokens/${tokenId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to revoke token');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
    },
  });

  const tokens: Token[] = data?.tokens || [];

  const copyToken = () => {
    if (newToken) {
      navigator.clipboard.writeText(newToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const subNavItems = [
    { to: '/security', label: 'Password' },
    { to: '/security/tokens', label: 'API Tokens' },
    { to: '/security/sessions', label: 'Sessions' },
    { to: '/security/two-factor', label: 'Two-Factor' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">API Tokens</h1>
        <p className="text-muted-foreground">Manage your personal API access tokens</p>
      </div>

      <SubNav items={subNavItems} />

      <div className="max-w-2xl space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-foreground">Your Tokens</h3>
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Token
          </Button>
        </div>

        {newToken && (
          <Card className="border-yellow-500/30 bg-yellow-500/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-yellow-400">
                <Key className="h-4 w-4" />
                New token created! Copy it now - you won't see it again.
              </div>
              <div className="mt-3 flex items-center gap-2">
                <code className="flex-1 break-all rounded bg-background p-2 font-mono text-sm">
                  {newToken}
                </code>
                <Button variant="secondary" size="sm" onClick={copyToken}>
                  <Copy className="mr-2 h-4 w-4" />
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setNewToken(null)}>
                  Dismiss
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {showCreate && (
          <Card>
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="tokenName">Token Name</Label>
                  <Input
                    id="tokenName"
                    value={tokenName}
                    onChange={(e) => setTokenName(e.target.value)}
                    placeholder="e.g., CI/CD Pipeline"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => tokenName && createMutation.mutate(tokenName)}
                    disabled={!tokenName || createMutation.isPending}
                  >
                    Create
                  </Button>
                  <Button variant="outline" onClick={() => setShowCreate(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        )}

        {tokens.length === 0 && !isLoading && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Key className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-foreground">No API tokens yet</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Create a token to authenticate API requests
              </p>
            </CardContent>
          </Card>
        )}

        {tokens.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Active Tokens</CardTitle>
              <CardDescription>Tokens you've created for API access</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {tokens.map((token) => (
                  <div
                    key={token.id}
                    className="flex items-center justify-between rounded-lg border border-border p-4"
                  >
                    <div>
                      <div className="font-medium text-foreground">{token.name}</div>
                      <div className="text-sm text-muted-foreground">
                        <code className="font-mono">{token.prefix}...</code> · Created{' '}
                        {new Date(token.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => {
                        if (confirm('Revoke this token?')) {
                          deleteMutation.mutate(token.id);
                        }
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Revoke
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/security/tokens')({
  component: TokensPage,
});
